import {GetServerSidePropsContext} from "next";
import {ReactElement, useMemo} from "react";
import type {NextPageWithLayout} from "@pages/_app";
import {Layout} from "@components/layouts/layout";
import {Plugin} from "@hooks/plugins/plugin";
import {IProcessedPluginData} from "@hooks/plugins/IProcessedPluginsData";
import {IPluginsDataResponse} from "@hooks/plugins/IPluginsDataResponse";
import Link from "@components/elements/DocumentLink";
import {PluginsSortableList} from "@components/seo/PluginsSortableList";
import {getPluginMaintenanceInsights} from "@utils/pluginInsights";
import {PluginsGetter} from "../../server/api/data/PluginsGetter";
import {getDb} from "../../server/api/db";
import {runAfterResponse} from "@utils/afterResponse";
import {formatNumber} from "@utils/format";
import {useStableNow} from "@hooks/useStableNow";
import {CACHE_WARM_HEADERS, shouldWarmCache} from "@utils/cacheWarm";

const DESIRED_ALTERNATIVES = 20;

const trimPluginForHub = (candidate: any) => {
    const trimmed = {...candidate};
    trimmed.banners = undefined;
    trimmed.changelog = undefined;
    trimmed.description = undefined;
    trimmed.faq = undefined;
    trimmed.installation = undefined;
    trimmed.reviews = [];
    trimmed.screenshots = undefined;
    trimmed.icons = Array.isArray(trimmed.icons) ? trimmed.icons.slice(0, 1) : trimmed.icons;
    return trimmed;
};

export const getServerSideProps = async (context: GetServerSidePropsContext) => {
    context.res.setHeader(
        'Cache-Control',
        'public, s-maxage=3600, stale-while-revalidate=86400'
    );

    const renderedAt = Date.now();
    const pluginId = String(context.params?.id ?? "").trim();
    if (!pluginId) {
        return {notFound: true};
    }

    const db = getDb();
    const getter = new PluginsGetter();
    const response: IPluginsDataResponse = await getter.get({
        db,
        pluginid: pluginId,
        page: 1,
        per_page: 1,
    }) as IPluginsDataResponse;

    const plugin = response.data?.plugins?.[0];
    if (!plugin) {
        return {notFound: true};
    }

    const tagSlugs = Array.isArray(plugin.tags)
        ? plugin.tags
            .map((tag: any) => ({
                slug: String(tag.slug ?? ""),
                count: Number(tag.count ?? tag.plugins_count ?? 0),
            }))
            .filter((tag: any) => tag.slug)
            .sort((a: any, b: any) => (a.count || 0) - (b.count || 0))
            .map((tag: any) => tag.slug)
        : [];

    const alternatives: any[] = [];
    const seenPluginIds = new Set<string>([pluginId]);
    const tagSetSizes = [3, 2, 1];

    for (const size of tagSetSizes) {
        if (alternatives.length >= DESIRED_ALTERNATIVES) break;
        const tags = tagSlugs.slice(0, size);
        if (!tags.length) continue;

        const alternativesResponse: IPluginsDataResponse = await getter.get({
            db,
            tags,
            page: 1,
            per_page: Math.max(25, DESIRED_ALTERNATIVES * 2),
        }) as IPluginsDataResponse;

        for (const candidate of alternativesResponse.data?.plugins ?? []) {
            const candidateId = String(candidate?.pluginid ?? "");
            if (!candidateId || seenPluginIds.has(candidateId)) continue;
            seenPluginIds.add(candidateId);
            alternatives.push(trimPluginForHub(candidate));
            if (alternatives.length >= DESIRED_ALTERNATIVES) break;
        }
    }

        // Warm CDN cache for main plugin page and top alternatives after response is sent
    const baseUrl = process.env.NEXT_PUBLIC_WEBSITE_URL;
    if (baseUrl && shouldWarmCache(context.req)) {
        runAfterResponse(context.res, async () => {
            const urlsToWarm = [
                `${baseUrl}/plugins/${pluginId}`,
                ...alternatives.slice(0, 3).map((alt: any) => `${baseUrl}/plugins/${alt.pluginid}`),
            ];
            await Promise.allSettled(
                urlsToWarm.map(url =>
                    fetch(url, { method: 'GET', headers: CACHE_WARM_HEADERS }).catch(() => {})
                )
            );
        });
    }

    return {
            props: {
            pluginName: plugin.name,
            pluginId: plugin.pluginid,
            shortDescription: plugin.short_description ?? null,
            rating: plugin.rating,
            numRatings: plugin.num_ratings,
            activeInstalls: plugin.active_installs,
            downloaded: plugin.downloaded ?? null,
            lastUpdatedTimestamp: plugin.last_updated?.timestamp ?? null,
            minimumWordpressVersion: plugin.minimum_wordpress_version ?? null,
            testedWordpressVersion: plugin.last_wordpress_compatible_version ?? null,
            minimumPhpVersion: plugin.minimum_php_version ?? null,
            stringifyPluginData: JSON.stringify(plugin),
            stringifyAlternativesData: JSON.stringify(alternatives),
            renderedAt,
        },
    };
};

const AlternativesPage: NextPageWithLayout = (props: any) => {
    const now = useStableNow(props.renderedAt);
    const pluginData = useMemo<IProcessedPluginData | null>(() => {
        if (!props.stringifyPluginData) return null;
        try {
            return Plugin.processApiResponse(JSON.parse(props.stringifyPluginData), now ?? props.renderedAt);
        } catch {
            return null;
        }
    }, [props.stringifyPluginData, now, props.renderedAt]);

    const alternatives = useMemo<IProcessedPluginData[]>(() => {
        if (!props.stringifyAlternativesData) return [];
        try {
            const parsed = JSON.parse(props.stringifyAlternativesData);
            if (!Array.isArray(parsed)) return [];
            return parsed.map((item) => Plugin.processApiResponse(item, now ?? props.renderedAt));
        } catch {
            return [];
        }
    }, [props.stringifyAlternativesData, now, props.renderedAt]);

    if (!pluginData) {
        return (
            <div className="flex items-center justify-center h-full p-8">
                <div className="text-center text-gray-600 dark:text-gray-300">Plugin not found.</div>
            </div>
        );
    }

    const maintenance = getPluginMaintenanceInsights(pluginData, now ?? props.renderedAt);
    const marketplaceLabel = pluginData.marketplace === 'woocommerce' ? 'WooCommerce Marketplace' : 'WordPress.org';

    return (
        <div className="flex flex-col gap-6 p-4 md:p-8 max-w-6xl mx-auto">
            <div className="flex flex-col gap-2">
                <h1 className="text-2xl md:text-4xl font-bold">
                    Alternatives to {pluginData.name}
                </h1>
                {pluginData.short_description && (
                    <p className="text-gray-700 dark:text-gray-300">{pluginData.short_description}</p>
                )}
                <div className="flex flex-row flex-wrap gap-4 text-sm">
                    <Link
                        href={`/plugins/${pluginData.pluginid}`}
                        className="text-blue-500 dark:text-blue-400 hover:underline"
                    >
                        View full plugin page
                    </Link>
                    <a
                        href={pluginData.wordpress_link.url}
                        className="text-blue-500 dark:text-blue-400 hover:underline"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {marketplaceLabel} listing
                    </a>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-gray-200 dark:border-gray-700 rounded-md p-4">
                    <div className="font-semibold">Snapshot</div>
                    <div className="mt-2 text-sm text-gray-700 dark:text-gray-300 flex flex-col gap-1">
                        <div>Rating: {pluginData.rating.toFixed(1)}/5 ({formatNumber(pluginData.num_ratings)} reviews)</div>
                        <div>Downloads: {formatNumber(pluginData.downloaded ?? 0)}</div>
                        <div>Active installs: {formatNumber(pluginData.active_installs)}</div>
                        <div>Maintenance score: {maintenance.score}/100</div>
                    </div>
                </div>
                <div className="border border-gray-200 dark:border-gray-700 rounded-md p-4">
                    <div className="font-semibold">Compatibility</div>
                    <div className="mt-2 text-sm text-gray-700 dark:text-gray-300 flex flex-col gap-1">
                        <div>Requires WordPress: {pluginData.minimum_wordpress_version || "Unknown"}</div>
                        <div>Tested up to: {pluginData.last_wordpress_compatible_version || "Unknown"}</div>
                        <div>Requires PHP: {pluginData.minimum_php_version || "Unknown"}</div>
                    </div>
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <h2 className="text-xl md:text-2xl font-semibold">Top alternatives</h2>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                    These alternatives are selected programmatically using overlapping tags and then sorted by your chosen metric.
                </p>
            </div>

            <PluginsSortableList plugins={alternatives} compareBasePluginId={pluginData.pluginid} nowTimestamp={now}/>
        </div>
    );
};

AlternativesPage.getLayout = function getLayout(page: ReactElement, props: any) {
    const baseUrl = String(process.env.NEXT_PUBLIC_WEBSITE_URL || "");
    const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
    const canonicalUrl = normalizedBaseUrl ? `${normalizedBaseUrl}/alternatives-to/${props.pluginId}` : `/alternatives-to/${props.pluginId}`;
    const imageUrl = normalizedBaseUrl ? `${normalizedBaseUrl}/catspyjamas.jpg` : "/catspyjamas.jpg";

    const nowSeconds = typeof props.renderedAt === "number" ? props.renderedAt / 1000 : null;
    const daysSinceUpdate = nowSeconds !== null && typeof props.lastUpdatedTimestamp === "number"
        ? Math.max(0, Math.floor((nowSeconds - props.lastUpdatedTimestamp) / 86400))
        : null;

    const descriptionParts = [
        `Alternatives to ${props.pluginName}.`,
        typeof props.rating === "number" && typeof props.numRatings === "number"
            ? `Rating ${props.rating.toFixed(1)}/5 from ${formatNumber(props.numRatings)} reviews.`
            : null,
        typeof props.activeInstalls === "number" ? `Active installs ${formatNumber(props.activeInstalls)}.` : null,
        typeof props.downloaded === "number" ? `Downloads ${formatNumber(props.downloaded)}.` : null,
        daysSinceUpdate !== null ? `Last updated ${daysSinceUpdate} days ago.` : null,
        props.minimumWordpressVersion ? `Requires WordPress ${props.minimumWordpressVersion}.` : null,
        props.minimumPhpVersion ? `Requires PHP ${props.minimumPhpVersion}.` : null,
    ].filter(Boolean);

    const rawDescription = descriptionParts.join(" ");
    const description = rawDescription.length > 280 ? `${rawDescription.slice(0, 277)}...` : rawDescription;

    return (
        <Layout
            title={`Alternatives to ${props.pluginName}`}
            description={description}
            url={canonicalUrl}
            image={imageUrl}
        >
            {page}
        </Layout>
    );
};

export default AlternativesPage;
